import {
  IonButton,
  IonButtons,
  IonChip,
  IonCol,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { closeCircleOutline } from 'ionicons/icons'
import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IOption } from '../../../common/common.types'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { IPHIComponent } from '../../../components/phi/phi.component'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import { getAge, transformDateWith4DigitYear, transformSSN } from '../../../utilities'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { getAgeUnitOptionId, isValidNemsisDOB } from '../../../utilities/dateTime.util'
import { getHomeSelector } from '../../home/selectors/getHome.selector'
import { getRelationshipAndGuarantorOptionsSelector } from '../../options/selectors/relationshipsAndGuarantorsOptions.selectors'
import ValidationService from '../../validation/services/validation.services'
import { getRelationshipsAndGuarantors } from '../selectors/getRelationshipsAndGuarantors.selector'
import {
  addRelationshipsAndGuarantors,
  clearRelationshipsAndGuarantorsData,
  removeRelationshipsAndGuarantors,
  setAge,
  setAgeUnit,
  setDOB,
  setDriversLicense,
  setDriversLicenseState,
  setFirstName,
  setLastName,
  setMiddleInitial,
  setNextOfKin,
  setRelationship,
  setRelationshipsAndGuarantorsData,
  setSameResidence,
  setSSN,
  setSuffix,
  setUnknownName,
} from '../store/relationshipsAndGuarantors.action'
import { RelationshipsAndGuarantorsConstants } from '../store/relationshipsAndGuarantors.constants'
import { initialRelationshipsAndGuarantors } from '../store/relationshipsAndGuarantors.reducers'
import './RelationshipsAndGuarantors.content.css'
import { IRelationshipsAndGuarantors } from '../store/relationshipsAndGuarantors.types'
import { IInsuranceInformationState } from '../../insuranceInformation/store/insuranceInformation.types'
import { getInsuranceInformationStateSelector } from '../../insuranceInformation/selectors'
import { showAlert } from '../../../components/alert/store/alert.actions'

const RelationshipsAndGuarantorsContent: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const { t } = useTranslation()
  const [flag, setFlag] = useState(true)
  const [isFocused, setIsFocused] = useState(false)
  const { isTablet: isTabletDevice } = useDeviceMetrics()

  const relationshipAndGuarantorOptions = useSelector(getRelationshipAndGuarantorOptionsSelector)
  const { relationshipOptions, suffixOptions, ageUnits, driversLicenseStateOptions } = relationshipAndGuarantorOptions
  const relationshipAndGuarantors = useSelector(getRelationshipsAndGuarantors)
  const { relationshipsAndGuarantorsData, relationshipsAndGuarantorsList } = relationshipAndGuarantors
  const {
    relationship,
    nextOfKin,
    sameResidence,
    firstName,
    middleInitial,
    lastName,
    suffix,
    unknownName,
    dob,
    age,
    ageUnit,
    ssn,
    driversLicense,
    driversLicenseState,
  } = relationshipsAndGuarantorsData
  const { insuranceInformationList }: IInsuranceInformationState = useSelector(getInsuranceInformationStateSelector)

  const isDataBlank =
    JSON.stringify(relationshipsAndGuarantorsData) === JSON.stringify(initialRelationshipsAndGuarantors)
  const { hideComponent } = useSelector(getHomeSelector)
  const dispatch = useDispatch()

  const onFocus = () => {
    setIsFocused(true)
  }
  const onBlur = () => {
    setIsFocused(false)
  }
  const setFirstNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setFirstName(e.detail.value))
  }

  const setLastNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setLastName(e.detail.value))
  }
  const setMiddleInitialOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setMiddleInitial(e.detail.value))
  }
  const setSuffixOnChange = (option: IOption) => {
    dispatch(setSuffix(option))
  }
  const setUnknownNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setUnknownName(e.detail.checked))
    if (e.detail.checked) {
      dispatch(setFirstName(''))
      dispatch(setMiddleInitial(''))
      dispatch(setLastName('Unknown'))
      dispatch(setSuffix(null))
    } else {
      dispatch(setLastName(''))
    }
  }
  const setRelationshipOnChange = (option: IOption) => {
    dispatch(setRelationship(option))
  }

  const setNextOfKinOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setNextOfKin(e.detail.checked))
  }

  const setSameResidenceOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setSameResidence(e.detail.checked))
  }
  const setDobOnChange = (numbers: string[]) => {
    let newDate = null
    if (numbers.length) {
      newDate = transformDateWith4DigitYear(numbers.join(''))
    }
    dispatch(setDOB(newDate))
    if (newDate && isValidNemsisDOB(newDate)) {
      dispatch(setAge(getAge(newDate)))
      const id = getAgeUnitOptionId(newDate)
      const ageUnitOption = ageUnits.filter((option: IOption) => String(option.id) === id)
      dispatch(setAgeUnit(ageUnitOption ? ageUnitOption[0] : null))
    } else if (!newDate) {
      dispatch(setAge(null))
      dispatch(setAgeUnit(null))
      ValidationService.clearValidation(RelationshipsAndGuarantorsConstants.SET_AGE)
    }
  }

  const setAgeOnChange = (numbers: string[]) => {
    dispatch(setAge(numbers.length ? numbers.join('') : null))
  }

  const setAgeUnitOnChange = (option: IOption) => {
    dispatch(setAgeUnit(option))
  }

  const setSSNOnChange = (numbers: string[]) => {
    let newSSN = null
    if (numbers.length) {
      newSSN = transformSSN(numbers.join(''))
    }
    dispatch(setSSN(newSSN))
  }

  const setDriversLicenseOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setDriversLicense(e.detail.value))
  }

  const setDLStateOnChange = (option: IOption) => {
    dispatch(setDriversLicenseState(option))
  }

  const onAddRelationshipsAndGuarantors = () => {
    dispatch(addRelationshipsAndGuarantors())
  }

  const onClearRelationshipsAndGuarantors = () => {
    dispatch(clearRelationshipsAndGuarantorsData())
  }

  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  useEffect(() => {
    if (flag) {
      firstName && dispatch(setFirstName(firstName))
      middleInitial && dispatch(setMiddleInitial(middleInitial))
      lastName && dispatch(setLastName(lastName))
      dob && dispatch(setDOB(dob))
      age && dispatch(setAge(age))
      ssn && dispatch(setSSN(ssn))
      driversLicense && dispatch(setDriversLicense(driversLicense))
    }
  }, [flag])

  const handleRemoveChip = (e: BaseSyntheticEvent, key: string) => {
    e.stopPropagation()
    const isUsedInInsuranceInfo = insuranceInformationList.find(
      (iInfo) => iInfo.insured && iInfo.insured.id?.toString() === key
    )
    if (isUsedInInsuranceInfo) {
      dispatch(
        showAlert({
          message: t('relationshipsAndGuarantors.deleteRelationshipError'),
        })
      )
      return
    }
    dispatch(removeRelationshipsAndGuarantors(key))
  }

  const handleChipClick = (e: BaseSyntheticEvent, data: IRelationshipsAndGuarantors) => {
    e.stopPropagation()
    if (data.ptid) {
      return
    }
    const randgData = relationshipsAndGuarantorsList.find((rgInfo) => rgInfo.key === data.key)
    if (randgData) {
      dispatch(setFirstName(randgData.firstName))
      dispatch(setLastName(randgData.lastName))
      dispatch(setMiddleInitial(randgData.middleInitial))
      dispatch(setDOB(randgData.dob))
      dispatch(setAge(randgData.age))
      dispatch(setSSN(randgData.ssn))
      dispatch(setDriversLicense(randgData.driversLicense))
      dispatch(setDriversLicenseState(randgData.driversLicenseState))
      dispatch(setRelationshipsAndGuarantorsData(randgData))
    }
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('relationshipsAndGuarantors.addGuarantorsInformation')}
        </IonLabel>
      </IonItemDivider>

      <IonRow className='ion-padding-start'>
        {relationshipsAndGuarantorsList &&
          relationshipsAndGuarantorsList.map((data, index) => (
            <IonChip key={data.key} onClick={(e) => handleChipClick(e, data)} disabled={!!data.ptid}>
              <IonLabel className='label-wrap'>{`${data.firstName} ${data.lastName}`}</IonLabel>
              {!data.ptid && (
                <IonIcon
                  date-testid={`clear_filter_icon_${index}`}
                  class='ion-align-self-center'
                  size='small'
                  icon={closeCircleOutline}
                  color='Contrast'
                  onClick={(e) => handleRemoveChip(e, data.key)}
                />
              )}
            </IonChip>
          ))}
      </IonRow>
      <IonList>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Relationship'
              selectedOption={relationship}
              listHeader={t('relationshipsAndGuarantors.relationship')}
              label={t('relationshipsAndGuarantors.relationship')}
              options={relationshipOptions}
              action={setRelationshipOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <IonItem key='Toggle_NektOfKin' lines='none'>
              <IonLabel className='extra-padding'>{t('relationshipsAndGuarantors.nextOfKin')}</IonLabel>
              <IonToggle
                slot='start'
                name='nextOfKin'
                checked={nextOfKin}
                onIonChange={(e) => setNextOfKinOnChange(e)}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        {hideComponent && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <IonItem key='Toggle_SameResidence' lines='none'>
                  <IonLabel className='extra-padding'>{t('relationshipsAndGuarantors.sameResidence')}</IonLabel>
                  <IonToggle
                    slot='start'
                    name='sameResidence'
                    checked={sameResidence}
                    onIonChange={(e) => setSameResidenceOnChange(e)}
                    color='zoll-blue-secondary'
                  />
                </IonItem>
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
        <IonRow>
          <IonCol className='picklist'>
            <SInput
              isPHI
              label={t('relationshipsAndGuarantors.firstName')}
              dataTestID='FirstName'
              inputValue={firstName}
              onChangeAction={setFirstNameOnChange}
              disabled={unknownName}
            />
            <Validation id={RelationshipsAndGuarantorsConstants.SET_FIRST_NAME} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
          <IonCol className='picklist'>
            <SInput
              isPHI
              label={t('relationshipsAndGuarantors.middleInitial')}
              dataTestID='MiddleInitial'
              inputValue={middleInitial}
              onChangeAction={setMiddleInitialOnChange}
              disabled={unknownName}
            />
            <Validation
              id={RelationshipsAndGuarantorsConstants.SET_MIDDLE_INITIAL}
              showReduxStateErrorsOnlyFlag={true}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SInput
              isPHI
              label={t('relationshipsAndGuarantors.lastName')}
              dataTestID='LastName'
              inputValue={lastName}
              onChangeAction={setLastNameOnChange}
              disabled={unknownName}
            />
            <Validation id={RelationshipsAndGuarantorsConstants.SET_LAST_NAME} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Suffix'
              selectedOption={suffix}
              listHeader={t('relationshipsAndGuarantors.suffix')}
              options={suffixOptions}
              action={setSuffixOnChange}
              label={t('relationshipsAndGuarantors.suffix')}
              disabled={unknownName}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <IonItem key='Toggle_UnknownName' lines='none'>
              <IonLabel className='extra-padding'>{t('relationshipsAndGuarantors.unknownName')}</IonLabel>
              <IonToggle
                slot='start'
                name='unknownName'
                checked={unknownName}
                onIonChange={(e) => setUnknownNameOnChange(e)}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              isPHI
              elementId='DOB'
              dataTestId='DOB'
              values={dob ? dob.toString().split('') : []}
              label={t('relationshipsAndGuarantors.DOB')}
              onNumPadClick={setDobOnChange}
              action={RelationshipsAndGuarantorsConstants.SET_DOB}
            />
            <Validation id={RelationshipsAndGuarantorsConstants.SET_DOB} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              isPHI
              elementId='Age'
              dataTestId='Age'
              values={age ? age.toString().split('') : []}
              label={t('relationshipsAndGuarantors.Age')}
              onNumPadClick={setAgeOnChange}
              action={RelationshipsAndGuarantorsConstants.SET_AGE}
            />
            <Validation id={RelationshipsAndGuarantorsConstants.SET_AGE} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='AgeUnit'
              selectedOption={ageUnit}
              listHeader={t('relationshipsAndGuarantors.AgeUnit')}
              label={t('relationshipsAndGuarantors.AgeUnit')}
              options={ageUnits}
              action={setAgeUnitOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              isPHI
              elementId='SSN'
              dataTestId='SSN'
              values={ssn ? ssn.toString().split('') : []}
              label={t('relationshipsAndGuarantors.SSN')}
              onNumPadClick={setSSNOnChange}
              action={RelationshipsAndGuarantorsConstants.SET_SSN}
            />
            <Validation id={RelationshipsAndGuarantorsConstants.SET_SSN} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SInput
              isPHI
              label={t('relationshipsAndGuarantors.DriversLicenseNumber')}
              dataTestID='DriversLicenseNumber'
              inputValue={driversLicense}
              onChangeAction={setDriversLicenseOnChange}
              onFocusAction={onFocus}
              onBlurAction={onBlur}
            />
            <Validation
              id={RelationshipsAndGuarantorsConstants.SET_DRIVERS_LICENSE}
              showReduxStateErrorsOnlyFlag={true}
            />
          </IonCol>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='DriverLicenseState'
              selectedOption={driversLicenseState}
              listHeader={t('relationshipsAndGuarantors.DriversLicenseState')}
              label={t('relationshipsAndGuarantors.DriversLicenseState')}
              options={driversLicenseStateOptions}
              action={setDLStateOnChange}
            />
          </IonCol>
        </IonRow>
      </IonList>
      <IonRow className='ion-float-right'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              disabled={isDataBlank}
              className='btn btn-standard mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => onClearRelationshipsAndGuarantors()}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              disabled={isDataBlank}
              className='btn btn-standard btn-primary'
              expand='block'
              onClick={() => onAddRelationshipsAndGuarantors()}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonRow>
      {isFocused && (
        <IonItemDivider color='light' className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'} />
      )}
    </>
  )
}

export default RelationshipsAndGuarantorsContent
